import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as yup from 'yup'
import { FormikHelpers } from 'formik'
import { useNewTranslation } from 'hooks/'
import { selectBusinessTypesOptions } from 'state/constants'
import { TeamState, createMember, MemberState, updateMember } from 'state/teams'
import { MODAL_TYPE } from '.'
import Modal, { AddMemberValuesState } from './index'
import { createGetRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { selectIsInternalUser } from 'state/profile'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'

interface CreateMemberProps {
    type: MODAL_TYPE
    onClose: () => void
    team: TeamState
    member: MemberState
}

const CreateMember: React.FC<CreateMemberProps> = ({ type, onClose, team, member }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const businessTypeOptions = useSelector(selectBusinessTypesOptions)
    const isInternalUser = useSelector(selectIsInternalUser)
    const isCreateMode = type === MODAL_TYPE.CREATE_MEMBER
    const isEditMode = type === MODAL_TYPE.EDIT_MEMBER
    const isLoading = useSelector(createGetRequestsAreLoading([STATE_KEYS.CreateMember]))
    const accountTypeOptions = useMemo(() => {
        return team?.roles
            .filter(role => {
                if (isInternalUser) {
                    return true
                }
                if (member?.role.name === 'Starred') {
                    return role.name === 'Starred' || role.name === 'Normal'
                }
                if (member?.role.name === 'Admin') {
                    return role.name === 'Admin' || role.name === 'Normal'
                }
                if (member?.role.name === 'Account Manager') {
                    return role.name === 'Account Manager' || role.name === 'Normal'
                }
                return role.name === 'Normal'
            })
            .map(role => ({ value: role.uuid, label: role.name }))
    }, [team])

    return (
        <Modal
            size="m"
            title={
                isCreateMode
                    ? t('modalWindow.addMemberTitle', {
                          teamName: team.name,
                      })
                    : t('modalWindow.editMemberTitle')
            }
            subtext={isCreateMode ? t('modalWindow.addMemberSubtitle') : ''}
            submitButtonText={isCreateMode ? t('button.sendInvite') : t('button.saveChanges')}
            validationSchema={yup.object({
                name: yup.string().required(t('errorMessage.validation.enterMemberName')),
                email: yup
                    .string()
                    .required(t('errorMessage.validation.enterEmailAddress'))
                    .test({
                        message: () =>
                            t('errorMessage.validation.enterValidEmail', {
                                email: team.is_internal ? 'abc@partipost.com' : 'abc@gmail.com',
                            }),
                        test: (value: string) => {
                            const isInvalidInternalEmail = team.is_internal
                                ? /@partipost.com\s*$/.test(value)
                                : /^\S+@\S+\.\S+$/.test(value)

                            return isInvalidInternalEmail
                        },
                    }),
                role: yup.string().required(t('errorMessage.validation.selectRole')),
                business_type: yup
                    .mixed()
                    .when([], () =>
                        team.is_internal
                            ? yup.mixed().nullable().notRequired()
                            : yup.mixed().nullable().required(t('errorMessage.validation.selectCompanyType')),
                    ),
            })}
            initialValues={{
                name: member ? member.name : '',
                email: member ? member.email : '',
                role: isCreateMode && !isInternalUser ? accountTypeOptions[0]?.value : member?.role?.uuid || '', // For external users in create mode, pre-select the Normal role
                business_type: member ? member.business_type : '',
            }}
            onSubmit={(values: AddMemberValuesState, actions: FormikHelpers<any>) => {
                let updatedValues = {
                    teamUuid: team.uuid,
                    name: values.name,
                    email: values.email,
                    role: values.role,
                    resetForm: actions.resetForm,
                }
                if (!team.is_internal) {
                    updatedValues = Object.assign({ business_type: values.business_type }, updatedValues)
                }
                if (isCreateMode) {
                    dispatch(createMember(updatedValues))
                } else if (isEditMode) {
                    dispatch(updateMember({ ...updatedValues, memberUuid: member.uuid }))
                    onClose()
                }
            }}
            onClose={onClose}
            isLoading={isLoading}
            loadingText={t('button.sendingInvitationLoadingState')}
        >
            <Modal.TextInput
                name="name"
                label={t('modalWindow.enterMemberNameHeader')}
                isRequired
                width="100%"
                isDisabled={isLoading}
                autoComplete={'off'}
            />
            <Modal.TextInput
                name="email"
                label={t('modalWindow.enterEmailAddressHeader')}
                isRequired
                width="100%"
                placeholder={t('placeholderText.enterEmailAddress')}
                isDisabled={isLoading}
                autoComplete={'off'}
            />
            <FormikRadioButtonGroup
                name="role"
                title={t('manageTeams.teamPage.roleTableLabel')}
                isRequired
                subtitle={t('modalWindow.selectRoleSubtext')}
                options={accountTypeOptions}
                orientation="horizontal"
                isDisabled={isLoading}
            />
            {!team.is_internal && (
                <FormikRadioButtonGroup
                    name="business_type"
                    title={t('modalWindow.enterMemberCompanyTypeHeader')}
                    isRequired
                    subtitle={t('modalWindow.selectCompanyTypeSubtext')}
                    options={businessTypeOptions}
                    orientation="horizontal"
                    isDisabled={isLoading}
                />
            )}
        </Modal>
    )
}

export default CreateMember
